import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import WaitlistForm from "../components/qhearts/WaitlistForm";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/sonner";

const HEART = "/assets/qheart-product.png";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: "easeOut" },
};

// Small orbiting heart used in the cluster around the main heart
const SmallHeart = ({ className, delay = 0, y = 10, duration = 5.5, rotate = 0 }) => (
  <motion.div
    className={`absolute pointer-events-none ${className}`}
    initial={{ opacity: 0, scale: 0.7 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1, delay, ease: "easeOut" }}
  >
    <motion.img
      src={HEART}
      alt=""
      aria-hidden="true"
      draggable="false"
      className="w-full h-auto object-contain select-none"
      style={{
        transform: `rotate(${rotate}deg)`,
        filter:
          "drop-shadow(0 20px 30px rgba(225,29,72,0.20)) drop-shadow(0 6px 10px rgba(0,0,0,0.06))",
      }}
      animate={{ y: [0, -y, 0] }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </motion.div>
);

export default function ComingSoon() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  const scrollToId = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="min-h-screen bg-white text-qh-ink font-body antialiased"
      data-testid="stealth-page"
    >
      <Toaster richColors position="top-center" />

      {/* HEADER — large centered logo */}
      <header className="px-6 sm:px-10 pt-6 sm:pt-8">
        <div className="max-w-6xl mx-auto flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center"
            data-testid="stealth-logo"
          >
            <img
              src="/assets/qhearts-logo-final.png"
              alt="QHearts — Keep Dating real."
              className="h-20 sm:h-24 lg:h-28 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>
        </div>
      </header>

      {/* HERO — fully centered */}
      <section
        className="relative px-6 sm:px-10 pt-10 sm:pt-14 pb-16 sm:pb-20 text-center"
        data-testid="stealth-hero"
      >
        <div className="max-w-5xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="font-display font-medium tracking-tight text-qh-ink leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
            data-testid="stealth-headline"
          >
            Finde, was dein <span className="text-qh-rose">Herz</span> wirklich
            sucht.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
            className="mt-8 mx-auto max-w-4xl text-lg sm:text-xl text-qh-slate leading-relaxed"
            data-testid="stealth-subhead"
          >
            QHearts ist eine neue Plattform für echte Begegnungen. Wir entwickeln
            neue Wege, Menschen wieder authentisch miteinander zu verbinden —
            dort, wo die schönsten Begegnungen entstehen:{" "}
            <span className="text-qh-ink font-medium">im echten Leben.</span>
          </motion.p>
        </div>

        {/* HEART CLUSTER — centered, big + 4 orbiting small hearts */}
        <div
          className="relative mt-14 sm:mt-16 mx-auto flex items-center justify-center w-full max-w-[560px] h-[380px] sm:h-[460px] lg:h-[520px]"
          data-testid="stealth-hero-visual"
        >
          <div className="pointer-events-none absolute inset-0 mx-auto rounded-full bg-[radial-gradient(closest-side,rgba(225,29,72,0.14),transparent_70%)] blur-3xl" />

          {/* Small hearts — 4 around */}
          <SmallHeart
            className="left-2 sm:left-6 top-4 sm:top-8 w-14 sm:w-20"
            delay={0.5}
            y={9}
            duration={5.5}
            rotate={-16}
          />
          <SmallHeart
            className="right-2 sm:right-6 top-2 sm:top-6 w-16 sm:w-24"
            delay={0.7}
            y={12}
            duration={6.5}
            rotate={14}
          />
          <SmallHeart
            className="left-0 sm:left-2 bottom-2 sm:bottom-6 w-16 sm:w-20"
            delay={0.9}
            y={8}
            duration={7}
            rotate={20}
          />
          <SmallHeart
            className="right-0 sm:right-4 bottom-4 sm:bottom-10 w-14 sm:w-20"
            delay={1.1}
            y={10}
            duration={6}
            rotate={-22}
          />

          {/* Big heart — center */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: "easeOut" }}
            className="relative"
          >
            <motion.img
              src={HEART}
              alt="QHearts"
              draggable="false"
              className="w-56 sm:w-72 lg:w-80 h-auto object-contain select-none"
              style={{
                filter:
                  "drop-shadow(0 50px 70px rgba(225,29,72,0.30)) drop-shadow(0 14px 24px rgba(0,0,0,0.10))",
              }}
              animate={{ y: [0, -14, 0] }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.6 }}
          className="mt-10"
        >
          <a href="#more" onClick={scrollToId("more")}>
            <Button
              variant="ghost"
              className="h-11 rounded-full text-qh-slate hover:text-qh-ink hover:bg-transparent px-2 text-sm font-medium"
              data-testid="stealth-cta-secondary"
            >
              Mehr erfahren ↓
            </Button>
          </a>
        </motion.div>
      </section>

      {/* SECTION — Reale Begegnungen (centered, tighter) */}
      <section
        id="more"
        className="px-6 sm:px-10 py-20 sm:py-24 border-t border-qh-line text-center"
        data-testid="stealth-section-real"
      >
        <div className="max-w-3xl mx-auto">
          <motion.h2
            {...fadeUp}
            className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-qh-ink leading-[1.05]"
          >
            Reale <span className="italic text-qh-rose">Begegnungen.</span>
          </motion.h2>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-8 space-y-5 text-lg sm:text-xl text-qh-slate leading-relaxed"
          >
            <p>
              Digitale Kommunikation ist heute selbstverständlich.
              <br className="hidden sm:block" />
              Echte Begegnungen bleiben jedoch etwas Besonderes.
            </p>
            <p>
              QHearts entwickelt eine Plattform, die Menschen dabei unterstützt,
              sich wieder einfacher kennenzulernen — authentisch, unkompliziert
              und im echten Leben.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WAITLIST — single CTA moment, centered */}
      <section
        id="waitlist"
        className="px-6 sm:px-10 py-20 sm:py-24 bg-[#FFFBFB] border-t border-qh-line text-center"
        data-testid="stealth-waitlist"
      >
        <div className="max-w-2xl mx-auto">
          <motion.h2
            {...fadeUp}
            className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-qh-ink leading-[1.05]"
          >
            Sei von Anfang an{" "}
            <span className="italic text-qh-rose">dabei.</span>
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.12 }}
            className="mt-6 text-lg text-qh-slate leading-relaxed"
          >
            Melde dich jetzt unverbindlich an und erfahre als Erste oder Erster,
            sobald QHearts startet.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.22 }}
            className="mt-10 flex justify-center"
          >
            <WaitlistForm
              showNameField
              stacked
              ctaLabel="Jetzt auf Warteliste eintragen"
              successMessage="Vielen Dank. Wir informieren dich zum offiziellen Start."
            />
          </motion.div>
        </div>
      </section>

      {/* INVESTOR — dezent, centered */}
      <section
        className="px-6 sm:px-10 py-16 sm:py-20 border-t border-qh-line text-center"
        data-testid="stealth-investor"
      >
        <div className="max-w-2xl mx-auto">
          <motion.p
            {...fadeUp}
            className="text-xs uppercase tracking-[0.28em] font-semibold text-qh-slate/80"
          >
            Für Investoren
          </motion.p>
          <motion.h3
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-4 font-display font-medium text-2xl sm:text-3xl text-qh-ink leading-snug"
          >
            Sind Sie an einem persönlichen Austausch interessiert?
          </motion.h3>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-8"
          >
            <a
              href="mailto:j.bender@qhearts.me?subject=Investor%20Deck%20Anfrage"
              data-testid="investor-cta"
            >
              <Button
                variant="outline"
                className="h-11 rounded-full border-qh-ink text-qh-ink hover:bg-qh-ink hover:text-white px-6 text-sm font-medium transition-all hover:-translate-y-0.5"
              >
                Investor Deck anfragen
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* FOOTER — larger logo, centered layout */}
      <footer
        className="border-t border-qh-line bg-white px-6 sm:px-10 py-12 text-center"
        data-testid="stealth-footer"
      >
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-6">
          <Link
            to="/"
            className="inline-flex items-center"
            data-testid="footer-logo"
          >
            <img
              src="/assets/qhearts-logo-final.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
          </Link>

          <a
            href="mailto:j.bender@qhearts.me"
            className="text-sm text-qh-slate hover:text-qh-ink transition-colors"
            data-testid="footer-email"
          >
            j.bender@qhearts.me
          </a>

          <nav className="flex items-center gap-6 text-sm text-qh-slate">
            <Link
              to="/impressum"
              className="hover:text-qh-ink transition-colors"
              data-testid="footer-impressum"
            >
              Impressum
            </Link>
            <Link
              to="/datenschutz"
              className="hover:text-qh-ink transition-colors"
              data-testid="footer-datenschutz"
            >
              Datenschutz
            </Link>
          </nav>

          <div className="text-xs text-qh-slate/70">
            © {new Date().getFullYear()} QHearts. Keep Dating real.
          </div>
        </div>
      </footer>
    </div>
  );
}
